import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Copy, Languages, Save } from 'lucide-react';
import { Button, Dialog, Input, Spinner } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useSaveStructure, useSiteStructure } from '@/hooks/useGeo';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { idempotencyKey } from '@/utils/idempotency';

type Mode = 'rows' | 'list';

const MODES: readonly { readonly mode: Mode; readonly label: string }[] = [
  { mode: 'rows', label: 'По строкам' },
  { mode: 'list', label: 'Списком' },
];

/** Pasted text as one translation per line, without the empty lines a paste ends with. */
function linesOf(text: string): string[] {
  const lines = text.split(/\r?\n/).map((line) => line.trim());
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

/**
 * The words of a site's template structure in the site's language (ADR-0047):
 * the tagline, menu labels, category names, page titles. Read like a list —
 * the original, and its translation beside it — or handled all at once: the
 * originals copied out, translated anywhere, the result pasted back in the
 * same order. An empty translation shows the original. Saving applies the
 * structure again.
 */
export function StructureSheet({ siteId, open, onClose }: { readonly siteId: string; readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const structure = useSiteStructure(siteId, { enabled: open });
  const strings = useMemo(() => structure.data?.strings ?? [], [structure.data]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<Mode>('rows');
  const [pasted, setPasted] = useState('');

  useEffect(() => {
    if (!open || !structure.data) return;
    setDraft(Object.fromEntries(structure.data.strings.map((item) => [item.key, item.translation ?? ''])));
    setMode('rows');
    setPasted('');
  }, [open, structure.data]);

  const save = useSaveStructure(siteId, {
    onSuccess: () => {
      toast.success('Перевод сохранён', 'Применяется на сайте');
      onClose();
    },
  });

  const groups = useMemo(() => {
    const byContext = new Map<string, { key: string; text: string }[]>();
    for (const item of strings) {
      const list = byContext.get(item.context) ?? [];
      list.push({ key: item.key, text: item.text });
      byContext.set(item.context, list);
    }
    return [...byContext.entries()];
  }, [strings]);

  const lines = linesOf(pasted);
  const listFits = lines.length === strings.length;
  const fromList = (): Record<string, string> => Object.fromEntries(strings.map((item, index) => [item.key, lines[index] ?? '']));
  // What is saved: the pasted list when there is one, else the rows.
  const result = mode === 'list' && lines.length > 0 ? fromList() : draft;
  const left = strings.filter((item) => !(result[item.key] ?? '').trim()).length;

  const switchTo = (next: Mode) => {
    if (next === 'rows' && lines.length > 0 && listFits) setDraft(fromList());
    setMode(next);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(strings.map((item) => item.text).join('\n'));
      toast.success('Строки скопированы', 'Переведите их целиком и вставьте сюда');
    } catch {
      toast.error('Не удалось скопировать', 'Браузер не дал доступа к буферу обмена');
    }
  };

  return (
    <Dialog
      open={open}
      title="Перевод структуры"
      description={left > 0 ? `Без перевода: ${left} из ${strings.length}` : 'Всё переведено'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            icon={Save}
            busy={save.isPending}
            disabled={!structure.data || (mode === 'list' && lines.length > 0 && !listFits)}
            onClick={() => save.mutate({ input: { strings: result }, key: idempotencyKey('site.structure') })}
          >
            Сохранить
          </Button>
        </>
      }
    >
      {structure.isPending ? (
        <Spinner />
      ) : structure.isError ? (
        <ErrorState error={structure.error} />
      ) : groups.length === 0 ? (
        <p className="muted row-tight">
          <Languages size={15} aria-hidden="true" />
          В структуре шаблона нет текста для перевода.
        </p>
      ) : (
        <div className="stack stack-loose">
          <div className="segmented segmented-text" role="group" aria-label="Как переводить">
            {MODES.map((option) => (
              <button key={option.mode} type="button" aria-pressed={mode === option.mode} onClick={() => switchTo(option.mode)}>
                {option.label}
              </button>
            ))}
          </div>

          {mode === 'rows' ? (
            groups.map(([context, items]) => (
              <section key={context} className="stack stack-tight">
                <h3 className="geo-heading">{context}</h3>
                <div className="translate-list">
                  {items.map((item) => (
                    <label key={item.key} className="translate-row">
                      <span className="translate-source">{item.text}</span>
                      <Input
                        value={draft[item.key] ?? ''}
                        placeholder="Без перевода"
                        aria-label={`Перевод: ${item.text}`}
                        onChange={(event) => setDraft((current) => ({ ...current, [item.key]: event.target.value }))}
                      />
                    </label>
                  ))}
                </div>
              </section>
            ))
          ) : (
            <div className="stack">
              <div className="row-between row-wrap">
                <span className="muted text-sm">Скопируйте строки, переведите их целиком в любом переводчике и вставьте ниже — по строке на каждую, в том же порядке.</span>
                <Button size="sm" icon={Copy} onClick={() => void copy()}>
                  Скопировать строки
                </Button>
              </div>
              <textarea
                className="input translate-paste"
                rows={Math.min(strings.length + 1, 14)}
                value={pasted}
                placeholder={strings.map((item) => item.text).join('\n')}
                aria-label="Переведённые строки"
                spellCheck={false}
                onChange={(event) => setPasted(event.target.value)}
              />
              {lines.length === 0 ? null : listFits ? (
                <p className="field-hint">Строк: {lines.length} — все на месте</p>
              ) : (
                <p className="field-error">
                  Строк: {lines.length}, а нужно {strings.length}. Проверьте, что переводчик не склеил и не разбил строки.
                </p>
              )}
            </div>
          )}
          {save.isError ? <p className="field-error">{errorText(save.error, 'Не удалось сохранить перевод')}</p> : null}
        </div>
      )}
    </Dialog>
  );
}
